import React from "react";
import { EmptyIllustration } from "./EmptyIllustration";

/**
 * EmptyState — issue #750 § 5.10. Two shapes, because the record's empty
 * state (#760, #702 — "Nothing on it yet", four rows that say when they
 * fill, no zeros) and the coach-portal's empty state (#756, "No players
 * yet.") are drawn differently, not two sizes of the same layout.
 *
 * `shape="record"`: `eyebrow` + `title` + `lede`, then an optional card
 * headed by `rowsLabel` and a list of `rows` (`{ label, note }`) divided by
 * hairlines. `titleVariant="lg"` reaches `.nw-title-lg` (26px) —
 * `EmptyRecord.dc.html:57` draws this screen at 26px, and decision 1 of
 * #750 § 4 names `EmptyRecord` as one of the five 26px record screens.
 * `shape="portal"`: a centred card with an icon well, a line and prose.
 */
export function EmptyState({
  shape = "record",
  eyebrow = null,
  title = null,
  titleVariant = "default", // "default" (24px, .nw-title) | "lg" (26px, .nw-title-lg)
  lede = null,
  rowsLabel = null,
  rows = [],
  // Issue #760 — `EmptyRecord.dc.html:75-76` draws two more lines INSIDE
  // the same rows card, after the divided rows themselves: the "none of
  // them starts at nought" rule, and the two named cases a row can stay
  // empty even after a match ("not out", "didn't bowl"). Neither is a row
  // (no right-hand "fills when" line) and neither is an `InfoRow` (the
  // brief's own ruling keeps both inside this card, not beside it) — a
  // plain trailing footer is the shape the drawing actually uses, and it
  // is optional so the coach-portal caller (`shape="portal"`, unaffected)
  // and any future `shape="record"` caller with nothing to add after the
  // rows cost nothing.
  footer = null,
  icon = null,
  // #1618 (D442): an engineering drawing (a key of `ILLUSTRATIONS`) that takes the icon's place above the line.
  illustration = null,
  line = null,
  prose = null,
  action = null,
  className = "",
  "data-testid": testId,
  ...rest
}) {
  if (shape === "portal") {
    return (
      <div
        data-testid={testId}
        className={`flex flex-col items-center text-center ${className}`}
        style={{
          background: "var(--nw-surface)",
          borderRadius: "var(--nw-radius-card)",
          border: "1px solid var(--nw-line)",
          padding: "28px 20px",
        }}
        {...rest}
      >
        {illustration ? (
          <EmptyIllustration name={illustration} style={{ marginBottom: 10 }} />
        ) : icon && (
          <div
            aria-hidden="true"
            className="flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "var(--nw-navy-tint)",
              color: "var(--nw-navy)",
              marginBottom: 14,
            }}
          >
            {icon}
          </div>
        )}
        {line && (
          <div className="nw-body-strong" style={{ marginBottom: 6 }}>
            {line}
          </div>
        )}
        {/* Issue #813: `CoachPortalEmpty.dc.html:68-69` draws this prose at
            14px, not the 15px `.nw-body` gave it — the only current caller
            of `shape="portal"` (grep-verified), so this is a same-screen
            fix, not a kit-wide one. `.nw-lede` is already 14px in the kit
            (`index.css`); its own colour token (`--nw-ink-lede`,
            `#475569`) differs from the drawing's `#64748B` here, so the
            inline override (unchanged, already correct per #813 — "the
            colour is right") stays and continues to win over the class's
            default. */}
        {prose && (
          <div className="nw-lede" style={{ color: "var(--nw-ink-muted)" }}>
            {prose}
          </div>
        )}
        {action && <div style={{ marginTop: 18, width: "100%" }}>{action}</div>}
      </div>
    );
  }

  // "record" — the default.
  return (
    <div data-testid={testId} className={className} {...rest}>
      {eyebrow && (
        <div className="nw-eyebrow" style={{ marginBottom: 6 }}>
          {eyebrow}
        </div>
      )}
      {title && (
        <div className={titleVariant === "lg" ? "nw-title-lg" : "nw-title"} style={{ marginBottom: 6 }}>
          {title}
        </div>
      )}
      {lede && (
        <div className="nw-lede" style={{ marginBottom: "var(--nw-space-stack)" }}>
          {lede}
        </div>
      )}
      {(rowsLabel || rows.length > 0) && (
        // PR #846 fix round, B1 — `EmptyRecord.dc.html`'s own rows card is
        // `display: flex; flex-direction: column; gap: 10px; padding: 16px`,
        // with every row and every hairline divider a DIRECT flex child at
        // full card-content width — never a per-row `padding`/`border-
        // bottom` (that shape measured padding 0/row-pitch 48px/edge-to-
        // edge dividers against the drawing's padding 16px/row-pitch
        // ~41px/dividers inset inside the padding). This is the exact
        // structure the drawing's own markup uses — 16/10 are the drawn
        // numbers verbatim, not yet a reusable token (this shape has one
        // caller, `RecordEmptyScreen.jsx` — grep-verified), so they are
        // literal here rather than invented into `index.css`.
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 16,
            background: "var(--nw-surface)",
            borderRadius: "var(--nw-radius-card)",
            border: "1px solid var(--nw-line)",
          }}
        >
          {/* `EmptyRecord.dc.html:62` draws this as the rows card's first
              child — §5.10 names it, and it was missing entirely. No
              padding of its own any more (B1): the card's own 16px already
              insets it, and the previous per-element "12px 14px 0" doubled
              that inset and added an unwanted top gap besides. */}
          {rowsLabel && <div className="nw-section">{rowsLabel}</div>}
          {rows.map((row, i) => (
            <React.Fragment key={row.label ?? i}>
              {/* B1 — `lineHeight: 1.3` here, not on the shared `.nw-body`
                  class (this row is its only caller — grep-verified):
                  neither span the drawing draws for this row sets its own
                  line-height, and the app's global Tailwind base line-
                  height (1.5) rendered this row at 23px instead of the
                  drawn 20px, over-shooting the measured 41px pitch to
                  ~44px. 1.3 (19.5px for the 15px label, the taller of the
                  two baseline-aligned spans) is what closes that gap. */}
              <div className="flex items-baseline justify-between" style={{ lineHeight: 1.3 }}>
                <span className="nw-body">{row.label}</span>
                {/* `EmptyRecord.dc.html:63-69` draws the note at 13px
                    `#94A3B8`, not the 12px `.nw-help` this used to render. */}
                {row.note && (
                  <span style={{ fontSize: 13, color: "var(--nw-ink-faint)" }}>{row.note}</span>
                )}
              </div>
              {/* B1 — a real hairline `<div>` between rows, never the last
                  row's own `border-bottom`: `EmptyRecord.dc.html` draws
                  three separate `height: 1px; background: #F1F5F9` divs
                  between the four rows, each its own flex child (so it
                  gets the SAME 10px `gap` on both sides as a row does —
                  measured drawn pitch is 41px: 20px row + 10 + 1 + 10),
                  not a border painted on the row above it. */}
              {i < rows.length - 1 && (
                <div style={{ height: 1, background: "var(--nw-line-faint)" }} />
              )}
            </React.Fragment>
          ))}
          {/* B1 — rendered as a DIRECT flex child, not wrapped in a padded
              div: `footer` is typically a fragment of two `<p>` siblings
              (`RecordEmptyScreen.jsx`'s own two help paragraphs), and
              rendering it unwrapped is what lets each paragraph pick up
              the card's own 10px `gap` independently, matching the
              drawing's own two separate prose divs — a wrapper here would
              collapse them back into one flex item with no gap between. */}
          {footer}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
